// Audiotool's render of each track on its own — without leaving the producer's
// project changed.
//
// A render of a COMMIT renders the project as it stood then (checked:
// tools/audiotool-render-spike.ts). So, in the producer's project:
//
//   1. note the commit as it is — the full mix renders from there;
//   2. per track, one transaction that isolates it, then note that commit;
//   3. one transaction that puts every touched field back as it was;
//
// — a few seconds of edits in all — and only then render each noted commit, at
// leisure. The project's history keeps the isolating commits; its present is
// exactly what it was.
//
// Isolating a track means: every OTHER mixer channel muted, nothing soloed, no
// channel feeding the shared delay/reverb returns (their tails would leak in),
// and the track's own channel at unity (input gain AND fader) and centred —
// the seed carries its volume and pan separately, so a render that had them baked in would get them
// twice. An audio player's own output gain goes to unity for the same reason
// (pull-plan folds it into the track volume). A channel's EQ, compressor and
// the instrument's effect chain stay: they are the track's sound. Tracks that
// share the target's channel are switched off, so only it plays.

import type { SyncedDocument } from "@audiotool/nexus";
import type { NexusEntity } from "@audiotool/nexus/document";

import { renderProject, type RenderApi } from "./render.js";
import { transact } from "./transact.js";

type Doc = Pick<SyncedDocument, "createTransaction" | "queryEntities">;

export interface IsolationTarget {
  /** Caller's key for the track ("a0", "m2", …). */
  key: string;
  /** The Audiotool track entity. */
  trackId: string;
  /** Its mixer channel. */
  stripId: string;
}

export interface Pins {
  /** The project as it was — the full mix. */
  full: bigint;
  /** Per target key, the commit where that track plays alone. */
  tracks: Map<string, bigint>;
}

const COMMIT_WAIT_MS = 20_000;

/** Wait until the server's latest commit is past `after`; return it. */
async function nextCommit(api: RenderApi, projectName: string, after: bigint): Promise<bigint> {
  const t0 = Date.now();
  for (;;) {
    const now = await api.latestCommit(projectName);
    if (now > after) return now;
    if (Date.now() - t0 > COMMIT_WAIT_MS) throw new Error("Audiotool didn't record the change in time.");
    await new Promise((r) => setTimeout(r, 300));
  }
}

interface StripState {
  preGain: number;
  postGain: number;
  panning: number;
  isMuted: boolean;
  isSoloed: boolean;
  auxSendsAreActive: boolean;
}

/**
 * Pin the full mix and one isolated commit per target, then restore. Restores
 * even when pinning fails part-way; a target that can't be pinned is left out
 * of `tracks`, and the caller falls back for it.
 */
export async function pinIsolatedTracks(doc: Doc, api: RenderApi, projectName: string, targets: IsolationTarget[]): Promise<Pins> {
  const q = doc.queryEntities;
  const full = await api.latestCommit(projectName);
  const tracks = new Map<string, bigint>();
  if (targets.length === 0) return { full, tracks };

  const strips = q.ofTypes("mixerChannel").get();
  const original = new Map<string, StripState>(strips.map((s) => [s.id, stateOf(s)]));
  const allTracks = [...q.ofTypes("audioTrack").get(), ...q.ofTypes("noteTrack").get()];
  const enabled = new Map<string, boolean>(allTracks.map((t) => [t.id, t.fields.isEnabled.value]));
  const players = new Map<string, number>();
  for (const t of q.ofTypes("audioTrack").get()) {
    const p = q.getEntityAs(t.fields.player.value.entityId, "audioDevice");
    if (p) players.set(p.id, p.fields.gain.value);
  }
  // Which channel each track reaches — for "tracks sharing the target's channel".
  const stripOfTrack = new Map<string, string>(targets.map((t) => [t.trackId, t.stripId]));

  let last = full;
  try {
    for (const target of targets) {
      try {
        await transact(doc, (t) => {
          for (const s of strips) {
            const f = s.fields.faderParameters.fields;
            const mine = s.id === target.stripId;
            setIf(t, f.isMuted, !mine);
            setIf(t, f.isSoloed, false);
            setIf(t, s.fields.auxSendsAreActive, false);
            setIf(t, s.fields.preGain, mine ? 1 : original.get(s.id)!.preGain);
            setIf(t, f.postGain, mine ? 1 : original.get(s.id)!.postGain);
            setIf(t, f.panning, mine ? 0 : original.get(s.id)!.panning);
          }
          for (const tr of allTracks) {
            const sharing = tr.id !== target.trackId && stripOfTrack.get(tr.id) === target.stripId;
            setIf(t, tr.fields.isEnabled, tr.id === target.trackId ? true : sharing ? false : enabled.get(tr.id)!);
          }
          const track = allTracks.find((tr) => tr.id === target.trackId);
          const player = track && track.entityType === "audioTrack" ? q.getEntityAs(track.fields.player.value.entityId, "audioDevice") : null;
          for (const [id, gain] of players) {
            const p = q.getEntityAs(id, "audioDevice")!;
            setIf(t, p.fields.gain, player && id === player.id ? 1 : gain);
          }
        });
        last = await nextCommit(api, projectName, last);
        tracks.set(target.key, last);
      } catch (e) {
        console.warn(`[Audiotool] couldn't isolate track ${target.trackId}:`, e);
      }
    }
  } finally {
    await transact(doc, (t) => {
      for (const s of strips) {
        const o = original.get(s.id)!;
        const f = s.fields.faderParameters.fields;
        setIf(t, f.isMuted, o.isMuted);
        setIf(t, f.isSoloed, o.isSoloed);
        setIf(t, s.fields.auxSendsAreActive, o.auxSendsAreActive);
        setIf(t, s.fields.preGain, o.preGain);
        setIf(t, f.postGain, o.postGain);
        setIf(t, f.panning, o.panning);
      }
      for (const tr of allTracks) setIf(t, tr.fields.isEnabled, enabled.get(tr.id)!);
      for (const [id, gain] of players) setIf(t, q.getEntityAs(id, "audioDevice")!.fields.gain, gain);
    });
    await nextCommit(api, projectName, last).catch(() => {});
  }
  return { full, tracks };
}

function stateOf(s: NexusEntity<"mixerChannel">): StripState {
  const f = s.fields.faderParameters.fields;
  return {
    preGain: s.fields.preGain.value,
    postGain: f.postGain.value,
    panning: f.panning.value,
    isMuted: f.isMuted.value,
    isSoloed: f.isSoloed.value,
    auxSendsAreActive: s.fields.auxSendsAreActive.value,
  };
}

/** Write a field only when it changes — an unchanged write is a commit for nothing. */
function setIf<T extends number | boolean>(
  t: { update: (field: never, value: never) => void },
  field: { value: T },
  value: T,
): void {
  if (field.value !== value) (t.update as (f: unknown, v: unknown) => void)(field, value);
}

/** Render every pinned commit, a couple at a time. Failed renders are left out. */
export async function renderPins(
  api: RenderApi,
  projectName: string,
  pins: Pins,
  opts: { signal?: AbortSignal; concurrency?: number; maxSeconds?: number; skipFull?: boolean; onRendered?: (key: string) => void } = {},
): Promise<{ full: Uint8Array | null; tracks: Map<string, Uint8Array>; errors: string[] }> {
  const jobs: Array<{ key: string; commit: bigint }> = [
    ...(opts.skipFull ? [] : [{ key: "full", commit: pins.full }]),
    ...[...pins.tracks].map(([key, commit]) => ({ key, commit })),
  ];
  const done = new Map<string, Uint8Array>();
  const errors: string[] = [];
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < jobs.length) {
      const job = jobs[next++]!;
      try {
        const o = { ...(opts.signal ? { signal: opts.signal } : {}), ...(opts.maxSeconds ? { maxSeconds: opts.maxSeconds } : {}) };
        done.set(job.key, (await renderProject(api, projectName, job.commit, o)).wav);
        opts.onRendered?.(job.key);
      } catch (e) {
        console.warn(`[Audiotool] render of ${job.key} (commit ${String(job.commit)}) failed:`, e);
        const why = e instanceof Error ? e.message : String(e);
        if (!errors.includes(why)) errors.push(why);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(opts.concurrency ?? 2, jobs.length) }, worker));
  const full = done.get("full") ?? null;
  done.delete("full");
  return { full, tracks: done, errors };
}
